import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotate,
  faEye,
  faFileCsv,
  faFileExcel,
  faFilter,
  faSearch,
  faSpinner,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import toast from "react-hot-toast";
import {
  getCurlRequestResponseLogDetail,
  getCurlRequestResponseLogsFilterOptions,
  listCurlRequestResponseLogs,
} from "../../services/curlRequestResponseLogsApi";

const PER_PAGE_OPTIONS = [10, 20, 50, 100, 200];

const DEFAULT_FILTERS = {
  search_string: "",
  log_uid: "",
  status: "",
  source_function: "",
  request_method: "",
  response_http_code: "",
  reference_type: "",
  created_by: "",
  recipient: "",
  curl_errno: "",
  date_from: "",
  date_to: "",
  sort_by: "created_at",
  sort_direction: "desc",
};

const EXPORT_COLUMNS = [
  { key: "log_uid", label: "Log UID" },
  { key: "status", label: "Status" },
  { key: "source_function", label: "Source Function" },
  { key: "request_method", label: "Request Method" },
  { key: "request_url", label: "Request URL" },
  { key: "response_http_code", label: "HTTP Code" },
  { key: "curl_errno", label: "cURL Errno" },
  { key: "latency_ms", label: "Latency (ms)" },
  { key: "recipient", label: "Recipient" },
  { key: "reference_type", label: "Reference Type" },
  { key: "reference_id", label: "Reference ID" },
  { key: "created_by", label: "Created By" },
  { key: "created_at", label: "Created At" },
  { key: "status_message", label: "Status Message" },
];

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("en-PH");
};

const truncateText = (value, max = 60) => {
  const text = String(value ?? "");
  if (text.length <= max) return text || "-";
  return `${text.slice(0, max)}...`;
};

const getStatusBadgeClass = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "success") return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (normalized === "error") return "bg-rose-100 text-rose-700 border-rose-200";
  return "bg-amber-100 text-amber-700 border-amber-200";
};

const stringifyValue = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
};

const exportToCsv = (rows, fileName) => {
  const escapeCsvValue = (value) => {
    const stringValue = String(value ?? "");
    if (
      stringValue.includes('"') ||
      stringValue.includes(",") ||
      stringValue.includes("\n")
    ) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const headerLine = EXPORT_COLUMNS.map((column) => escapeCsvValue(column.label)).join(",");
  const rowLines = rows.map((row) =>
    EXPORT_COLUMNS.map((column) => {
      if (column.key === "created_at") {
        return escapeCsvValue(formatDateTime(row[column.key]));
      }
      return escapeCsvValue(row[column.key]);
    }).join(",")
  );

  const csvContent = [headerLine, ...rowLines].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const exportToExcel = async (rows, fileName) => {
  const XLSX = await import("xlsx");
  const exportRows = rows.map((row) => {
    const exportRow = {};
    EXPORT_COLUMNS.forEach((column) => {
      exportRow[column.label] =
        column.key === "created_at"
          ? formatDateTime(row[column.key])
          : row[column.key] ?? "";
    });
    return exportRow;
  });

  const worksheet = XLSX.utils.json_to_sheet(exportRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "cURL Logs");
  XLSX.writeFile(workbook, fileName);
};

const getPaginationWindow = (currentPage, totalPages) => {
  const maxVisible = 5;
  const pages = [];
  let start = Math.max(1, currentPage - 2);
  let end = Math.min(totalPages, start + maxVisible - 1);

  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1);
  }

  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }

  return pages;
};

const CurlRequestResponseLogsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [filterForm, setFilterForm] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [filterOptions, setFilterOptions] = useState({
    status: [],
    source_function: [],
    request_method: [],
    response_http_code: [],
    reference_type: [],
    created_by: [],
  });

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);

  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await getCurlRequestResponseLogsFilterOptions();
      setFilterOptions(response.data?.data || {});
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Failed to load cURL request/response log filter options."
      );
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = {
        page,
        per_page: perPage,
        ...appliedFilters,
      };

      Object.keys(params).forEach((key) => {
        if (params[key] === "" || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await listCurlRequestResponseLogs(params);
      const payload = response.data || {};
      const pagination = payload.pagination || {};

      setRows(Array.isArray(payload.data) ? payload.data : []);
      setTotalRecords(Number(pagination.total_records || 0));
      setTotalPages(Math.max(1, Number(pagination.total_pages || 1)));
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Failed to load cURL request/response logs."
      );
      setRows([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page, perPage]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const showingText = useMemo(() => {
    if (!totalRecords) return "No records";
    const from = (page - 1) * perPage + 1;
    const to = Math.min(page * perPage, totalRecords);
    return `Showing ${from} to ${to} of ${totalRecords}`;
  }, [page, perPage, totalRecords]);

  const paginationWindow = useMemo(
    () => getPaginationWindow(page, totalPages),
    [page, totalPages]
  );

  const handleApplyFilters = (event) => {
    event.preventDefault();
    setPage(1);
    setAppliedFilters({ ...filterForm });
  };

  const handleResetFilters = () => {
    setFilterForm(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setPage(1);
    setPerPage(20);
  };

  const handleSort = (sortByKey) => {
    setPage(1);
    setFilterForm((previous) => {
      const nextDirection =
        previous.sort_by === sortByKey && previous.sort_direction === "asc"
          ? "desc"
          : "asc";
      const nextForm = {
        ...previous,
        sort_by: sortByKey,
        sort_direction: nextDirection,
      };
      setAppliedFilters(nextForm);
      return nextForm;
    });
  };

  const openDetail = async (row) => {
    if (!row?.log_uid) return;
    setDetailOpen(true);
    setDetailLoading(true);
    setSelectedDetail(null);
    try {
      const response = await getCurlRequestResponseLogDetail(row.log_uid);
      setSelectedDetail(response.data?.data || null);
    } catch (requestError) {
      toast.error(
        requestError?.response?.data?.message ||
          "Failed to load log detail."
      );
      setSelectedDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedDetail(null);
  };

  const fetchRowsForExport = async () => {
    const params = {
      page: 1,
      per_page: 10000,
      for_export: "1",
      ...appliedFilters,
    };

    Object.keys(params).forEach((key) => {
      if (params[key] === "" || params[key] === null || params[key] === undefined) {
        delete params[key];
      }
    });

    const response = await listCurlRequestResponseLogs(params);
    return Array.isArray(response.data?.data) ? response.data.data : [];
  };

  const handleExportCsv = async () => {
    setExportingCsv(true);
    try {
      const exportRows = await fetchRowsForExport();
      if (!exportRows.length) {
        toast.info("No cURL logs to export.");
        return;
      }
      const fileName = `curl_request_response_logs_${new Date()
        .toISOString()
        .replace(/[:T]/g, "-")
        .split(".")[0]}.csv`;
      exportToCsv(exportRows, fileName);
    } catch (requestError) {
      toast.error(
        requestError?.response?.data?.message ||
          "Failed to export CSV."
      );
    } finally {
      setExportingCsv(false);
    }
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const exportRows = await fetchRowsForExport();
      if (!exportRows.length) {
        toast.info("No cURL logs to export.");
        return;
      }
      const fileName = `curl_request_response_logs_${new Date()
        .toISOString()
        .replace(/[:T]/g, "-")
        .split(".")[0]}.xlsx`;
      await exportToExcel(exportRows, fileName);
    } catch (requestError) {
      toast.error(
        requestError?.response?.data?.message ||
          "Failed to export Excel."
      );
    } finally {
      setExportingExcel(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white px-6 py-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              cURL Request Logs
            </h1>
            <p className="text-sm text-slate-600">
              Monitor API outbound requests and responses with traceable status, payload, and delivery diagnostics.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={fetchLogs}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faArrowsRotate} className="mr-2" />
              Refresh
            </button>
            <button
              type="button"
              onClick={handleExportCsv}
              disabled={loading || exportingCsv || exportingExcel}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-60"
            >
              <FontAwesomeIcon icon={faFileCsv} className="mr-2" />
              {exportingCsv ? "Exporting CSV..." : "CSV"}
            </button>
            <button
              type="button"
              onClick={handleExportExcel}
              disabled={loading || exportingCsv || exportingExcel}
              className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
              {exportingExcel ? "Exporting Excel..." : "Excel"}
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-6">
        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {typeof error === "string" ? error : JSON.stringify(error)}
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <form onSubmit={handleApplyFilters} className="grid grid-cols-1 gap-3 md:grid-cols-6">
            <div className="relative md:col-span-2">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={filterForm.search_string}
                onChange={(event) =>
                  setFilterForm((previous) => ({
                    ...previous,
                    search_string: event.target.value,
                  }))
                }
                placeholder="Search log UID, source, url, recipient..."
                className="w-full rounded-xl border border-slate-200 py-2 pl-9 pr-3 text-sm"
              />
            </div>

            <select
              value={filterForm.status}
              onChange={(event) =>
                setFilterForm((previous) => ({ ...previous, status: event.target.value }))
              }
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="">All Status</option>
              {(filterOptions.status || []).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <select
              value={filterForm.source_function}
              onChange={(event) =>
                setFilterForm((previous) => ({
                  ...previous,
                  source_function: event.target.value,
                }))
              }
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="">All Source Functions</option>
              {(filterOptions.source_function || []).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <select
              value={filterForm.request_method}
              onChange={(event) =>
                setFilterForm((previous) => ({
                  ...previous,
                  request_method: event.target.value,
                }))
              }
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="">All Methods</option>
              {(filterOptions.request_method || []).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <select
              value={filterForm.response_http_code}
              onChange={(event) =>
                setFilterForm((previous) => ({
                  ...previous,
                  response_http_code: event.target.value,
                }))
              }
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="">All HTTP Codes</option>
              {(filterOptions.response_http_code || []).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <select
              value={filterForm.reference_type}
              onChange={(event) =>
                setFilterForm((previous) => ({
                  ...previous,
                  reference_type: event.target.value,
                }))
              }
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="">All Reference Types</option>
              {(filterOptions.reference_type || []).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <select
              value={filterForm.created_by}
              onChange={(event) =>
                setFilterForm((previous) => ({
                  ...previous,
                  created_by: event.target.value,
                }))
              }
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="">All Created By</option>
              {(filterOptions.created_by || []).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <input
              type="text"
              value={filterForm.recipient}
              onChange={(event) =>
                setFilterForm((previous) => ({
                  ...previous,
                  recipient: event.target.value,
                }))
              }
              placeholder="Recipient"
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
            />

            <input
              type="text"
              value={filterForm.log_uid}
              onChange={(event) =>
                setFilterForm((previous) => ({
                  ...previous,
                  log_uid: event.target.value,
                }))
              }
              placeholder="Log UID"
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
            />

            <input
              type="number"
              min="0"
              value={filterForm.curl_errno}
              onChange={(event) =>
                setFilterForm((previous) => ({
                  ...previous,
                  curl_errno: event.target.value,
                }))
              }
              placeholder="cURL Errno"
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
            />

            <input
              type="date"
              value={filterForm.date_from}
              onChange={(event) =>
                setFilterForm((previous) => ({
                  ...previous,
                  date_from: event.target.value,
                }))
              }
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
            />

            <input
              type="date"
              value={filterForm.date_to}
              onChange={(event) =>
                setFilterForm((previous) => ({
                  ...previous,
                  date_to: event.target.value,
                }))
              }
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
            />

            <div className="flex items-center gap-2 md:col-span-6">
              <button
                type="submit"
                className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
              >
                <FontAwesomeIcon icon={faFilter} className="mr-2" />
                Apply Filters
              </button>
              <button
                type="button"
                onClick={handleResetFilters}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
              >
                Reset
              </button>
            </div>
          </form>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3 text-sm text-slate-600">
            <span>{showingText}</span>
            <div className="flex items-center gap-2">
              <span>Display per page</span>
              <select
                value={perPage}
                onChange={(event) => {
                  setPerPage(Number(event.target.value));
                  setPage(1);
                }}
                className="rounded-lg border border-slate-200 px-2 py-1"
              >
                {PER_PAGE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-700">
                <tr>
                  <th className="px-3 py-3 text-left">
                    <button type="button" onClick={() => handleSort("created_at")} className="font-semibold">
                      Created At
                    </button>
                  </th>
                  <th className="px-3 py-3 text-left">
                    <button type="button" onClick={() => handleSort("status")} className="font-semibold">
                      Status
                    </button>
                  </th>
                  <th className="px-3 py-3 text-left">
                    <button type="button" onClick={() => handleSort("source_function")} className="font-semibold">
                      Source Function
                    </button>
                  </th>
                  <th className="px-3 py-3 text-left">
                    <button type="button" onClick={() => handleSort("request_method")} className="font-semibold">
                      Method
                    </button>
                  </th>
                  <th className="px-3 py-3 text-left">Request URL</th>
                  <th className="px-3 py-3 text-left">
                    <button type="button" onClick={() => handleSort("response_http_code")} className="font-semibold">
                      HTTP
                    </button>
                  </th>
                  <th className="px-3 py-3 text-left">
                    <button type="button" onClick={() => handleSort("curl_errno")} className="font-semibold">
                      cURL Errno
                    </button>
                  </th>
                  <th className="px-3 py-3 text-left">
                    <button type="button" onClick={() => handleSort("latency_ms")} className="font-semibold">
                      Latency (ms)
                    </button>
                  </th>
                  <th className="px-3 py-3 text-left">
                    <button type="button" onClick={() => handleSort("recipient")} className="font-semibold">
                      Recipient
                    </button>
                  </th>
                  <th className="px-3 py-3 text-left">Status Message</th>
                  <th className="px-3 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-10 text-center text-slate-500">
                      <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                      Loading cURL request/response logs...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-10 text-center text-slate-500">
                      No cURL request/response logs found.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.log_uid}>
                      <td className="px-3 py-3">{formatDateTime(row.created_at)}</td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${getStatusBadgeClass(row.status)}`}>
                          {String(row.status || "").toUpperCase() || "-"}
                        </span>
                      </td>
                      <td className="px-3 py-3">{truncateText(row.source_function, 42)}</td>
                      <td className="px-3 py-3">{row.request_method || "-"}</td>
                      <td className="px-3 py-3" title={row.request_url || ""}>
                        {truncateText(row.request_url, 48)}
                      </td>
                      <td className="px-3 py-3">{row.response_http_code ?? "-"}</td>
                      <td className="px-3 py-3">{row.curl_errno ?? 0}</td>
                      <td className="px-3 py-3">{row.latency_ms ?? "-"}</td>
                      <td className="px-3 py-3" title={row.recipient || ""}>
                        {truncateText(row.recipient, 18)}
                      </td>
                      <td className="px-3 py-3" title={row.status_message || ""}>
                        {truncateText(row.status_message, 46)}
                      </td>
                      <td className="px-3 py-3">
                        <button
                          type="button"
                          onClick={() => openDetail(row)}
                          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                        >
                          <FontAwesomeIcon icon={faEye} className="mr-1" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 px-4 py-3">
            <div className="text-sm text-slate-600">Page {page} of {totalPages}</div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage((previous) => Math.max(1, previous - 1))}
                disabled={page <= 1}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
              >
                Prev
              </button>

              {paginationWindow.map((windowPage) => (
                <button
                  key={windowPage}
                  type="button"
                  onClick={() => setPage(windowPage)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
                    windowPage === page
                      ? "bg-slate-900 text-white"
                      : "border border-slate-200 text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {windowPage}
                </button>
              ))}

              <button
                type="button"
                onClick={() => setPage((previous) => Math.min(totalPages, previous + 1))}
                disabled={page >= totalPages}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {detailOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">cURL Log Detail</h3>
                <p className="text-xs text-slate-500">
                  {selectedDetail?.log_uid || "Loading detail..."}
                </p>
              </div>
              <button
                type="button"
                onClick={closeDetail}
                className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-slate-500 hover:bg-slate-100"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <div className="max-h-[78vh] space-y-4 overflow-auto p-5">
              {detailLoading ? (
                <div className="py-14 text-center text-slate-500">
                  <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                  Loading detail...
                </div>
              ) : !selectedDetail ? (
                <div className="py-14 text-center text-slate-500">
                  No detail available.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <p className="text-xs text-slate-500">Status</p>
                      <p className="text-sm font-semibold text-slate-800">
                        {selectedDetail.status || "-"}
                      </p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <p className="text-xs text-slate-500">HTTP Code</p>
                      <p className="text-sm font-semibold text-slate-800">
                        {selectedDetail.response_http_code ?? "-"}
                      </p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <p className="text-xs text-slate-500">Latency</p>
                      <p className="text-sm font-semibold text-slate-800">
                        {selectedDetail.latency_ms ?? "-"} ms
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="rounded-xl border border-slate-200 p-4">
                      <h4 className="mb-2 text-sm font-semibold text-slate-900">Request</h4>
                      <div className="space-y-2 text-xs text-slate-600">
                        <p><span className="font-semibold">Method:</span> {selectedDetail.request_method || "-"}</p>
                        <p><span className="font-semibold">URL:</span> {selectedDetail.request_url || "-"}</p>
                        <p><span className="font-semibold">Body Size:</span> {selectedDetail.request_body_size ?? 0}</p>
                      </div>
                      <div className="mt-3 space-y-2">
                        <div>
                          <p className="mb-1 text-xs font-semibold text-slate-700">Request Headers</p>
                          <pre className="max-h-48 overflow-auto rounded-lg bg-slate-900 p-3 text-[11px] text-slate-100">
                            {stringifyValue(selectedDetail.request_headers_json || selectedDetail.request_headers)}
                          </pre>
                        </div>
                        <div>
                          <p className="mb-1 text-xs font-semibold text-slate-700">Request Body</p>
                          <pre className="max-h-64 overflow-auto rounded-lg bg-slate-900 p-3 text-[11px] text-slate-100">
                            {stringifyValue(selectedDetail.request_body_json || selectedDetail.request_body)}
                          </pre>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 p-4">
                      <h4 className="mb-2 text-sm font-semibold text-slate-900">Response</h4>
                      <div className="space-y-2 text-xs text-slate-600">
                        <p><span className="font-semibold">HTTP Code:</span> {selectedDetail.response_http_code ?? "-"}</p>
                        <p><span className="font-semibold">cURL Errno:</span> {selectedDetail.curl_errno ?? 0}</p>
                        <p><span className="font-semibold">cURL Error:</span> {selectedDetail.curl_error || "-"}</p>
                        <p><span className="font-semibold">Body Size:</span> {selectedDetail.response_body_size ?? 0}</p>
                      </div>
                      <div className="mt-3 space-y-2">
                        <div>
                          <p className="mb-1 text-xs font-semibold text-slate-700">Response Headers</p>
                          <pre className="max-h-48 overflow-auto rounded-lg bg-slate-900 p-3 text-[11px] text-slate-100">
                            {stringifyValue(selectedDetail.response_headers_json || selectedDetail.response_headers)}
                          </pre>
                        </div>
                        <div>
                          <p className="mb-1 text-xs font-semibold text-slate-700">Response Body</p>
                          <pre className="max-h-64 overflow-auto rounded-lg bg-slate-900 p-3 text-[11px] text-slate-100">
                            {stringifyValue(selectedDetail.response_body_json || selectedDetail.response_body)}
                          </pre>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 p-4">
                    <h4 className="mb-2 text-sm font-semibold text-slate-900">Context</h4>
                    <div className="grid grid-cols-1 gap-2 text-xs text-slate-600 md:grid-cols-3">
                      <p><span className="font-semibold">Source:</span> {selectedDetail.source_function || "-"}</p>
                      <p><span className="font-semibold">Recipient:</span> {selectedDetail.recipient || "-"}</p>
                      <p><span className="font-semibold">Created By:</span> {selectedDetail.created_by || "-"}</p>
                      <p><span className="font-semibold">Reference Type:</span> {selectedDetail.reference_type || "-"}</p>
                      <p><span className="font-semibold">Reference ID:</span> {selectedDetail.reference_id || "-"}</p>
                      <p><span className="font-semibold">Created At:</span> {formatDateTime(selectedDetail.created_at)}</p>
                    </div>
                    <div className="mt-3">
                      <p className="mb-1 text-xs font-semibold text-slate-700">Request Context</p>
                      <pre className="max-h-52 overflow-auto rounded-lg bg-slate-900 p-3 text-[11px] text-slate-100">
                        {stringifyValue(selectedDetail.request_context_json || selectedDetail.request_context)}
                      </pre>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurlRequestResponseLogsPage;

